import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { reportsAPI, managerDashboardAPI } from '../../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BarChart3, Clock, Users, ListTodo, AlertTriangle, Timer } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { toast } from 'sonner';

const TeamReportsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overtime');
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [otReport, setOtReport] = useState([]);
  const [taskReport, setTaskReport] = useState([]);

  const loadReports = useCallback(async () => {
    if (!user?.company_id) return;
    setLoading(true);
    try {
      const [otRes, taskRes] = await Promise.all([
        reportsAPI.getOvertime(user.company_id, dateRange.start, dateRange.end).catch(() => ({ data: [] })),
        reportsAPI.getTasks(user.company_id, dateRange.start, dateRange.end).catch(() => ({ data: [] }))
      ]);
      setOtReport(otRes.data || []);
      setTaskReport(taskRes.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id, dateRange]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const totalOtHours = otReport.reduce((sum, r) => sum + (r.total_ot_hours || 0), 0);
  const totalTaskHours = taskReport.reduce((sum, r) => sum + (r.total_hours || 0), 0);

  return (
    <div className="space-y-6" data-testid="team-reports-page">
      <div>
        <h1 className="text-2xl font-bold">Team Reports</h1>
        <p className="text-muted-foreground">View overtime and task reports for your direct reports (hours only)</p>
      </div>

      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Report Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-[180px]"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-[180px]"
              />
            </div>
            <Button onClick={loadReports} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Timer className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total OT Hours</p>
                <p className="text-2xl font-bold">{totalOtHours.toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <ListTodo className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Task Hours</p>
                <p className="text-2xl font-bold">{totalTaskHours.toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Employees with OT</p>
                <p className="text-2xl font-bold">{otReport.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active on Tasks</p>
                <p className="text-2xl font-bold">{taskReport.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overtime" className="gap-2">
            <Timer className="h-4 w-4" />
            Overtime Report
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-2">
            <ListTodo className="h-4 w-4" />
            Task Report
          </TabsTrigger>
        </TabsList>

        {/* Overtime Tab */}
        <TabsContent value="overtime" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Overtime by Employee</CardTitle>
              <CardDescription>
                Showing overtime hours for your direct reports. 
                <span className="text-amber-600 ml-1">Note: Cost data is not visible to managers.</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Total OT Hours</TableHead>
                    <TableHead>Days with OT</TableHead>
                    <TableHead>Avg OT/Day</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {otReport.map(row => (
                    <TableRow key={row.employee_id}>
                      <TableCell className="font-medium">{row.employee_name}</TableCell>
                      <TableCell>{row.department_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-blue-600">
                          {row.total_ot_hours}h
                        </Badge>
                      </TableCell>
                      <TableCell>{row.days_with_ot}</TableCell>
                      <TableCell>{(row.total_ot_hours / row.days_with_ot).toFixed(1)}h</TableCell>
                    </TableRow>
                  ))}
                  {otReport.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No overtime data for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Task Time by Employee</CardTitle>
              <CardDescription>Time logged against tasks by your direct reports</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Total Hours</TableHead>
                    <TableHead>Task Breakdown</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taskReport.map(row => (
                    <TableRow key={row.employee_id}>
                      <TableCell className="font-medium">{row.employee_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-purple-600">
                          {row.total_hours}h
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {row.task_breakdown?.slice(0, 3).map((task, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {task.task_name}: {task.hours}h
                            </Badge>
                          ))}
                          {row.task_breakdown?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{row.task_breakdown.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {taskReport.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                        No task data for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info Banner */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Manager Access Restrictions</p>
              <p className="text-sm text-amber-700 mt-1">
                As a manager, you can view hours worked but not compensation data (salaries, pay rates, or costs). 
                Payroll and audit logs are restricted to Admin/Owner roles.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamReportsPage;
